/**
 * Blog document rendering modes:
 *   Type: 1 — existing card → modal (HTML Body) or PDF (pdfUrl)
 *   Type: 2 — full guide-style page at /posts/{slug}
 *
 * Type 2 required fields:
 *   Type: 2
 *   slug: string
 *   Caption, Image, Date
 *
 * Type 2 structured layout (full reading-guide look):
 *   title, intro
 *   seoTitle, seoDescription
 *   nav: [{ id, label }]
 *   sections: [ Section ]
 *
 * Section types:
 *   hero          — uses doc title + intro
 *   text          — { body }
 *   subtypeGrid   — { intro, items[{ title, body?, image, alt, linkTo?, linkLabel? }], columns?, proTip? }
 *   instruction   — { paragraphs[], highlights[], figure?, subsections[{ title, kind: "steps"|"text", steps?/body? }] }
 *   examples      — { intro, meters[{ image, alt }], answerKeyTitle, answers[{ value, note }] }
 *   bullets       — { intro, figure?, bullets[{ title, body }] }
 *   cta           — { paragraphs[], links[{ label, href, kind: "mailto"|"route" }] }
 *
 * If Type 2 and `sections` is omitted, Caption + Body HTML render in the guide chrome.
 */

import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "../firebase-config";

export const BLOG_TYPE_MODAL = 1;
export const BLOG_TYPE_GUIDE_PAGE = 2;

export function blogPagePath(slug) {
  return `/posts/${slug}`;
}

export function getBlogType(blog) {
  const raw = blog?.Type ?? blog?.type;
  const n = Number(raw);
  if (n === BLOG_TYPE_GUIDE_PAGE) return BLOG_TYPE_GUIDE_PAGE;
  // Legacy flag from earlier iteration
  if (blog?.openAsPage) return BLOG_TYPE_GUIDE_PAGE;
  return BLOG_TYPE_MODAL;
}

export function isGuideStyleBlog(blog) {
  return getBlogType(blog) === BLOG_TYPE_GUIDE_PAGE;
}

/** Attach `route` for Type 2 blogs so cards navigate instead of opening a modal. */
export function withGuidePageRoute(blog) {
  if (!blog) return blog;
  if (blog.route) return blog;
  if (isGuideStyleBlog(blog) && blog.slug) {
    return { ...blog, route: blogPagePath(blog.slug) };
  }
  return blog;
}

export async function fetchBlogBySlug(slug) {
  if (!slug) return null;

  const today = new Date().toISOString().split("T")[0];
  const blogCollection = collection(db, "Blogs");

  const bySlug = query(blogCollection, where("slug", "==", slug), limit(1));
  const snap = await getDocs(bySlug);
  let blog = null;

  if (!snap.empty) {
    const d = snap.docs[0];
    blog = { ...d.data(), id: d.id };
  } else {
    const all = await getDocs(blogCollection);
    const match = all.docs.find((d) => d.id === slug);
    if (match) blog = { ...match.data(), id: match.id };
  }

  if (!blog) return null;
  if (!isGuideStyleBlog(blog) && !blog.route) return null;

  try {
    const blogDate = new Date(blog.Date).toISOString().split("T")[0];
    if (blogDate > today) return null;
  } catch {
    return null;
  }

  return withGuidePageRoute(blog);
}
